import api from './api';

// ==================== PROFORMA ====================

export interface ProformaItem {
  id?: number;
  description: string;
  quantity: number;
  unit_price: number;
  total: number;
}

export interface Proforma {
  id: number;
  user_id: number;
  customer_id: number;
  case_id?: number;
  proforma_number: string;
  items: ProformaItem[];
  subtotal: number;
  tax_rate: number;
  tax_amount: number;
  discount: number;
  total: number;
  status: 'draft' | 'sent' | 'accepted' | 'rejected' | 'expired';
  notes?: string;
  due_date: string;
  issued_date: string;
  currency: string;
  created_at?: string;
  updated_at?: string;
  // For display purposes
  customer_name?: string;
  case_subject?: string;
}

export interface ProformaCreateInput {
  customer_id: number;
  case_id?: number;
  items: ProformaItem[];
  subtotal: number;
  tax_rate: number;
  tax_amount: number;
  discount: number;
  total: number;
  notes?: string;
  due_date: string;
  issued_date: string;
  currency: string;
}

export interface ProformaUpdateInput extends Partial<ProformaCreateInput> {
  id: number;
  status?: string;
}

// ==================== BILLS ====================

export interface BillItem {
  id?: number;
  description: string;
  quantity: number;
  unit_price: number;
  total: number;
}

export interface Bill {
  id: number;
  user_id: number;
  customer_id: number;
  customer?: any;
  case_id?: number;
  case_file?: any;
  proforma_id?: number;
  bill_number: string;
  items: BillItem[];
  subtotal: number;
  tax_rate: number;
  tax_amount: number;
  discount: number;
  total: number;
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
  payment_date?: string;
  payment_method?: string;
  notes?: string;
  due_date: string;
  issued_date: string;
  currency: string;
  created_at?: string;
  updated_at?: string;
  // For display purposes
  customer_name?: string;
  case_subject?: string;
  proforma_number?: string;
}

export interface BillCreateInput {
  user_id?: string;
  customer_id: number;
  case_id?: number;
  proforma_id?: number;
  items: BillItem[];
  subtotal: number;
  tax_rate: number;
  tax_amount: number;
  discount: number;
  total: number;
  notes?: string;
  due_date: string;
  issued_date: string;
  currency: string;
}

export interface BillUpdateInput extends Partial<BillCreateInput> {
  id: number;
  status?: string;
  payment_date?: string;
  payment_method?: string;
}

// ==================== REVENUES ====================

export interface RevenueSummary {
  collection_rate: number;
  total_invoiced: string;
  total_collected: string;
  total_pending: string;
  year: number;
}

export interface MonthlyRevenue {
  month: number;
  invoiced: string;
  collected: string;
  pending: string;
}

export interface TopClient {
  customer_id: number;
  customer_name: string;
  total_amount: string;
  bills_count: number;
}

// ==================== SERVICE ====================

const billingService = {
  // --- Proforma ---
  getProformas: async (userId: string) => {
    const response = await api.get(`/billing/proformas/${userId}`);
    return response.data.data;
  },

  getProformaById: async (proformaId: number, userId: string) => {
    const response = await api.get(`/billing/proformas/${proformaId}/${userId}`);
    return response.data;
  },

  createProforma: async (data: ProformaCreateInput) => {
    const response = await api.post('/billing/proformas', data);
    return response.data;
  },

  updateProforma: async (data: ProformaUpdateInput) => {
    const response = await api.put('/billing/proformas', data);
    return response.data;
  },

  deleteProforma: async (proformaId: number, userId: string) => {
    const response = await api.delete(`/billing/proformas/${proformaId}/${userId}`);
    return response.data;
  },

  updateProformaStatus: async (proformaId: number, status: string, userId: string) => {
    const response = await api.put('/billing/proformas/status', { proforma_id: proformaId, status, user_id: userId });
    return response.data;
  },

  exportProformaPdf: async (proformaId: number) => {
    const response = await api.get(`/billing/proformas/${proformaId}/pdf`, {
      responseType: 'blob'
    });
    return response.data;
  },

  searchProformas: async (userId: string, query: string) => {
    const response = await api.get(`/billing/proformas/search/${userId}?query=${query}`);
    return response.data;
  },

  // --- Bills ---
  getBills: async (userId: string) => {
    const response = await api.get(`/billing/bills/${userId}`);
    return response.data.data;
  },

  getBillById: async (billId: number, userId: string) => {
    const response = await api.get(`/billing/bills/${billId}/${userId}`);
    return response.data;
  },

  createBill: async (data: BillCreateInput) => {
    const response = await api.post('/billing/bills', data);
    return response.data;
  },

  updateBill: async (data: BillUpdateInput) => {
    const response = await api.put('/billing/bills', data);
    return response.data;
  },

  deleteBill: async (billId: number, userId: string) => {
    const response = await api.delete(`/billing/bills/${billId}/${userId}`);
    return response.data;
  },

  updateBillStatus: async (billId: number, status: string, userId: string) => {
    const response = await api.put('/billing/bills/status', { bill_id: billId, status, user_id: userId });
    return response.data;
  },

  markBillAsPaid: async (billId: number, paymentMethod: string, paymentDate: string) => {
    const response = await api.put('/billing/bills/pay', { bill_id: billId, payment_method: paymentMethod, payment_date: paymentDate });
    return response.data;
  },

  sendBill: async (billId: number) => {
    const response = await api.post(`/billing/bills/${billId}/send`);
    return response.data;
  },

  exportBillPdf: async (billId: number) => {
    const response = await api.get(`/billing/bills/${billId}/pdf`, {
      responseType: 'blob'
    });
    return response.data;
  },

  searchBills: async (userId: string, query: string) => {
    const response = await api.get(`/billing/bills/search/${userId}?query=${query}`);
    return response.data;
  },

  // --- Revenues ---
  getRevenueSummary: async (userId: string, year?: string) => {
    const url = year ? `/billing/revenues/summary/${userId}?year=${year}` : `/billing/revenues/summary/${userId}`;
    const response = await api.get(url);
    return response.data;
  },

  getMonthlyRevenue: async (userId: string, year?: string) => {
    const url = year ? `/billing/revenues/monthly/${userId}?year=${year}` : `/billing/revenues/monthly/${userId}`;
    const response = await api.get(url);
    return response.data;
  },

  getTopClients: async (userId: string, year?: string) => {
    const url = year ? `/billing/revenues/top-clients/${userId}?year=${year}` : `/billing/revenues/top-clients/${userId}`;
    const response = await api.get(url);
    return response.data;
  },
};

export default billingService;
